import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FloatingParticles from "../components/FloatingParticles";
const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";

const calculateAge = (dobString) => {
    if (!dobString) return "";
    // Handle MySQL datetime strings like "1995-06-06T00:00:00.000Z" or "1995-06-06 00:00:00"
    const datePart = String(dobString).split("T")[0].split(" ")[0];
    const [year, month, day] = datePart.split("-").map(Number);
    if (!year || !month || !day) return "";
    const today = new Date();
    let age = today.getFullYear() - year;
    const m = today.getMonth() + 1 - month; // getMonth() is 0-indexed
    if (m < 0 || (m === 0 && today.getDate() < day)) {
        age--;
    }
    return age;
};

const calculateBMI = (hVal, hUnit, wVal, wUnit) => {
    if (!hVal || !wVal || isNaN(parseFloat(hVal)) || isNaN(parseFloat(wVal))) return "--";
    let heightInMeters = parseFloat(hVal);
    let weightInKg = parseFloat(wVal);

    if (hUnit === "cm") heightInMeters /= 100;
    else if (hUnit === "in") heightInMeters *= 0.0254;
    else if (hUnit === "ft") heightInMeters *= 0.3048;

    if (wUnit === "lbs") weightInKg *= 0.453592;

    if (heightInMeters === 0) return "--";
    const bmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(1);
    
    let label = "";
    if (bmi < 18.5) label = " (Underweight)";
    else if (bmi >= 18.5 && bmi <= 24.9) label = " (Normal)";
    else if (bmi >= 25 && bmi <= 29.9) label = " (Overweight)";
    else label = " (Obese)";
    
    return bmi + label;
};

const UserProfile = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
        dob: "",
        gender: "",
        city: "",
        height: "",
        weight: "",
        blood_group: "",
        blood_pressure: ""
    });
    // Local states for split values
    const [hVal, setHVal] = useState("");
    const [hUnit, setHUnit] = useState("cm");
    const [wVal, setWVal] = useState("");
    const [wUnit, setWUnit] = useState("kg");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                let storedUser = null;
                try {
                    const raw = localStorage.getItem("user");
                    storedUser = raw && raw !== "undefined" ? JSON.parse(raw) : null;
                } catch {
                    storedUser = null;
                }
                const userId = storedUser?.id || storedUser?.userId;
                if (!userId) {
                    setLoading(false);
                    return;
                }
                const res = await axios.get(`${API}/api/users/profile/${userId}`);
                setUser(res.data);
                setFormData({
                    name: res.data.name || "",
                    email: res.data.email || "",
                    phone: res.data.phone || "",
                    address: res.data.address || "",
                    dob: res.data.dob ? res.data.dob.split("T")[0] : "",
                    gender: res.data.gender || "",
                    city: res.data.city || "",
                    height: res.data.height || "",
                    weight: res.data.weight || "",
                    blood_group: res.data.blood_group || "",
                    blood_pressure: res.data.blood_pressure || ""
                });

                // Parse metrics
                if (res.data.height) {
                    const hStr = String(res.data.height);
                    const match = hStr.match(/^([\d.]+)\s*(.*)$/);
                    if (match) { setHVal(match[1]); setHUnit(match[2] || "cm"); }
                    else { setHVal(hStr); setHUnit("cm"); }
                }
                if (res.data.weight) {
                    const wStr = String(res.data.weight);
                    const match = wStr.match(/^([\d.]+)\s*(.*)$/);
                    if (match) { setWVal(match[1]); setWUnit(match[2] || "kg"); }
                    else { setWVal(wStr); setWUnit("kg"); }
                }
            } catch (err) {
                toast.error("Failed to load profile details.");
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Update concatenated strings whenever local units/values change
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            height: hVal ? `${hVal} ${hUnit}` : "",
            weight: wVal ? `${wVal} ${wUnit}` : ""
        }));
    }, [hVal, hUnit, wVal, wUnit]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const targetId = user?.id || user?.userId;
            if (!targetId) {
                toast.error("User session expired. Please log in again.");
                return;
            }
            await axios.put(`${API}/api/users/profile/${targetId}`, formData);
            
            const updatedUser = { ...user, name: formData.name, email: formData.email };
            setUser(updatedUser);
            
            try {
                const raw = localStorage.getItem("user");
                const currentStored = raw && raw !== "undefined" ? JSON.parse(raw) : {};
                localStorage.setItem("user", JSON.stringify({ ...currentStored, name: formData.name, email: formData.email }));
                window.dispatchEvent(new Event("storage"));
            } catch {}
            
            toast.success("Profile updated successfully!");
            const prevRoute = sessionStorage.getItem("cb_prev_route");
            if (prevRoute) {
                sessionStorage.removeItem("cb_prev_route");
                sessionStorage.setItem("cb_profile_updated", "true");
                if (prevRoute === "/doctors") {
                    navigate("/dashboard");
                } else {
                    navigate(prevRoute);
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update profile.");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#f0f9ff] to-[#e0f2fe] flex justify-center items-center">
                <div className="text-xl font-bold text-blue-600 animate-pulse font-['Outfit']">Authenticating Profile...</div>
            </div>
        );
    }

    if (!user) return <div className="text-center mt-20 text-xl font-['Outfit']">Please login to view profile.</div>;

    return (
        <div className="min-h-[90vh] bg-gradient-to-br from-[#f0f9ff] to-[#e0f2fe] p-4 lg:p-8 flex justify-center items-start font-['Outfit'] relative overflow-hidden text-slate-800">
            <FloatingParticles />
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="w-full max-w-4xl relative z-10">
                {/* Header Space */}
                <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-extrabold text-[#0c4a6e] tracking-tight">{user.name}</h1>
                        <p className="text-base text-slate-500 font-medium mt-1">{user.email}</p>
                    </div>

                    {/* Prominent Verification Badge */}
                    {user.is_verified ? (
                        <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl shadow-lg shadow-emerald-500/30 transform hover:-translate-y-1 transition-all border border-emerald-400 backdrop-blur-md">
                            <span className="text-white text-xl">✨</span>
                            <div>
                                <div className="text-white font-extrabold tracking-wide uppercase text-sm leading-tight">Verified Patient</div>
                                <div className="text-emerald-50 font-medium text-xs">Patient ID: PT-{String(user.id).padStart(6, '0')}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 px-5 py-3 bg-white/60 backdrop-blur-md rounded-2xl border border-amber-200 text-amber-600 shadow-sm">
                            <span>⏳</span>
                            <div>
                                <div className="font-bold text-sm uppercase tracking-wide">Verification Pending</div>
                                <div className="text-amber-700 font-medium text-xs mt-0.5">User ID: PT-{String(user.id).padStart(6, '0')}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Profile Card */}
                <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] shadow-2xl shadow-blue-900/10 border border-white/60 overflow-hidden">
                    <div className="px-8 py-6 border-b border-blue-50 bg-white/40">
                        <h2 className="text-xl font-extrabold text-[#0c4a6e]">Personal Details</h2>
                        <p className="text-sm text-slate-500 font-medium mt-1">Update your medical demographic information to ensure accurate records.</p>
                    </div>

                    <form onSubmit={handleUpdate} className="p-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">👤 Full Name</label>
                                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium placeholder:text-slate-400"
                                    placeholder="Enter your full name" />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">📧 Email Address</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} required
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium placeholder:text-slate-400"
                                    placeholder="your.email@example.com" />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">📱 Phone Number</label>
                                <input type="text" name="phone" value={formData.phone} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium placeholder:text-slate-400"
                                    placeholder="+91 98765 43210" />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🎂 Date of Birth</label>
                                <input type="date" name="dob" value={formData.dob} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700" />
                            </div>
                            {/* Auto-calculated Age field */}
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🎯 Age</label>
                                <div className="w-full px-4 py-3 bg-blue-50/70 border border-blue-100 rounded-xl font-bold text-blue-700 text-base shadow-sm flex items-center gap-2">
                                    {formData.dob
                                        ? <><span className="text-2xl font-black text-blue-600">{calculateAge(formData.dob)}</span><span className="text-slate-500 font-medium text-sm">years old</span></>
                                        : <span className="text-slate-400 font-medium text-sm">Enter date of birth above</span>
                                    }
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🚻 Gender</label>
                                <select name="gender" value={formData.gender} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700 appearance-none">
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🏙️ City</label>
                                <input type="text" name="city" value={formData.city} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium placeholder:text-slate-400"
                                    placeholder="Current City" />
                            </div>
                            <div className="md:col-span-2 flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">📍 Primary Address</label>
                                <textarea name="address" value={formData.address} onChange={handleChange} rows="3"
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm resize-none font-medium placeholder:text-slate-400"
                                    placeholder="Street address, Apartment, Suite, etc."></textarea>
                            </div>
                        </div>

                        {/* Vitals Section */}
                        <div className="flex justify-between items-center px-8 py-4 mt-8 -mx-8 border-y border-blue-50 bg-blue-50/30">
                            <div>
                                <h2 className="text-xl font-extrabold text-[#0c4a6e]">Medical Vitals</h2>
                                <p className="text-sm text-slate-500 font-medium">Keep these up to date to help your doctors assist you better.</p>
                            </div>
                            <div className="text-right">
                                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">BMI</div>
                                <div className="text-lg font-black text-blue-600">{calculateBMI(hVal, hUnit, wVal, wUnit)}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 pt-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">📏 Height</label>
                                <div className="flex gap-2">
                                    <input type="number" value={hVal} onChange={(e) => setHVal(e.target.value)}
                                        className="flex-1 px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium"
                                        placeholder="Value" />
                                    <select value={hUnit} onChange={(e) => setHUnit(e.target.value)}
                                        className="w-24 px-2 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white outline-none shadow-sm font-medium text-slate-600">
                                        <option value="cm">cm</option>
                                        <option value="in">in</option>
                                        <option value="ft">ft</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">⚖️ Weight</label>
                                <div className="flex gap-2">
                                    <input type="number" value={wVal} onChange={(e) => setWVal(e.target.value)}
                                        className="flex-1 px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium"
                                        placeholder="Value" />
                                    <select value={wUnit} onChange={(e) => setWUnit(e.target.value)}
                                        className="w-24 px-2 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white outline-none shadow-sm font-medium text-slate-600">
                                        <option value="kg">kg</option>
                                        <option value="lbs">lbs</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">🩸 Blood Group</label>
                                <select name="blood_group" value={formData.blood_group} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium text-slate-700 appearance-none">
                                    <option value="">Select Group</option>
                                    <option value="A+">A+</option>
                                    <option value="A-">A-</option>
                                    <option value="B+">B+</option>
                                    <option value="B-">B-</option>
                                    <option value="AB+">AB+</option>
                                    <option value="AB-">AB-</option>
                                    <option value="O+">O+</option>
                                    <option value="O-">O-</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">💓 Blood Pressure</label>
                                <input type="text" name="blood_pressure" value={formData.blood_pressure} onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/60 border border-blue-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all shadow-sm font-medium placeholder:text-slate-400"
                                    placeholder="e.g. 120/80" />
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-blue-50 mt-4">
                            <button type="submit"
                                className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-[#0ea5e9] hover:from-blue-700 hover:to-[#0284c7] text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-1 flex items-center gap-2 text-lg">
                                <span>📥</span> Save Demographics
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
